import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/config/company";

function Logo() {
  return (
    <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo" aria-label={`${COMPANY.name} home`}>
      <span className="bg-white rounded-lg px-2.5 py-1.5 inline-flex items-center justify-center">
        <img src="/logo.png" alt={`${COMPANY.name} logo`} className="h-9 w-auto" />
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#081A33]/90 backdrop-blur-xl border-b border-white/10">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-teal focus:text-white focus:px-4 focus:py-2 focus:rounded"
        data-testid="skip-to-content-link"
      >
        Skip to main content
      </a>
      <nav
        aria-label="Primary"
        className={`container-site flex items-center justify-between transition-[padding] duration-300 ${scrolled ? "py-2.5" : "py-4"}`}
      >
        <Logo />
        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    isActive ? "text-white bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-request-consultation-btn"
            className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
          >
            Request a Consultation
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md text-white hover:bg-white/10"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </nav>
      {open && (
        <div id="mobile-menu" className="lg:hidden bg-[#081A33] border-t border-white/10" data-testid="mobile-menu">
          <ul className="container-site py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-md text-base font-medium ${
                      isActive ? "text-white bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-3">
              <Link
                to="/contact"
                data-testid="mobile-request-consultation-btn"
                className="flex items-center justify-center gap-2 bg-teal hover:bg-teal-dark text-white font-semibold px-5 py-3.5 rounded-full"
              >
                Request a Consultation
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
